import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  TextField,
  Paper,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import CustomerService from '../../services/customer.service';
import DataTable from '../../components/common/DataTable';
import { usePagination } from '../../hooks/usePagination';

const CustomerList: React.FC = () => {
  const navigate = useNavigate();
  const { pagination, setPagination, handlePageChange, handlePageSizeChange } = usePagination(25);
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        'pagination[page]': pagination.page + 1,
        'pagination[pageSize]': pagination.pageSize,
      };

      // Add search filter if search term exists
      if (searchTerm) {
        params['filters[$or][0][companyName][$containsi]'] = searchTerm;
        params['filters[$or][1][contactPerson][$containsi]'] = searchTerm;
        params['filters[$or][2][email][$containsi]'] = searchTerm;
      }

      const response = await CustomerService.getAll(params);
      const customersData = response.data?.data || [];
      const paginationMeta = response.data?.meta?.pagination || {};
      
      setCustomers(customersData);
      setPagination({
        total: paginationMeta.total || 0,
        pageCount: paginationMeta.pageCount || 0,
      });
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, searchTerm, setPagination]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== undefined) {
        handlePageChange(0);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, handlePageChange]);

  const handleDelete = async (id: number) => {
    if (window.confirm('Delete this customer?')) {
      try {
        await CustomerService.delete(id);
        fetchCustomers();
      } catch (error) {
        console.error('Error deleting customer:', error);
      }
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    // Reset to first page when search changes
    setPagination({ page: 0 });
  };

  const columns = [
    { id: 'customerNumber', label: 'Customer #' },
    {
      id: 'companyName',
      label: 'Company',
      format: (value: string) => value || '-',
    },
    { id: 'contactPerson', label: 'Contact Person' },
    { id: 'email', label: 'Email' },
    { id: 'phone', label: 'Phone' },
    {
      id: 'vinNumber',
      label: 'VIN Number',
      format: (value: string) => value || '-',
    },
    {
      id: 'plateNumber',
      label: 'Plate Number',
      format: (value: string) => value || '-',
    },
    {
      id: 'servicePackage',
      label: 'Service Package',
      renderCell: ({ value, row }: any) => {
        if (!value) return '-';
        const pkg = value?.data || value?.attributes || value;
        return pkg?.name || pkg?.code || '-';
      },
    },
    {
      id: 'customerType',
      label: 'Type',
      format: (value: string) => (
        <Chip label={value} size="small" color={value === 'business' ? 'primary' : 'default'} />
      ),
    },
    {
      id: 'isActive',
      label: 'Status',
      format: (value: boolean) => (
        <Chip label={value ? 'Active' : 'Inactive'} size="small" color={value ? 'success' : 'default'} />
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Customers</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/customers/create')}
        >
          Add Customer
        </Button>
      </Stack>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          label="Search customers..."
          variant="outlined"
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </Paper>

      <DataTable
        columns={columns}
        data={customers}
        loading={loading}
        page={pagination.page}
        rowsPerPage={pagination.pageSize}
        totalCount={pagination.total}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePageSizeChange}
        onView={(row) => navigate(`/customers/${row.id || row.attributes?.id}`)}
        onEdit={(row) => navigate(`/customers/${row.id || row.attributes?.id}/edit`)}
        onDelete={handleDelete}
      />
    </Box>
  );
};

export default CustomerList;

